import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Stage, Layer, Image, Circle, Rect, Arrow, Text, Transformer, Group } from 'react-konva';
import Konva from 'konva';
import useImage from 'use-image';
import './FrameEditor.css';
import { API_URL } from './Config';

// Custom hook to load image with crossOrigin for blur support
const ImageComponent = ({ src, onLoad }) => {
  const [image] = useImage(src, 'anonymous'); // Add crossOrigin support
  
  useEffect(() => {
    if (image && onLoad) {
      onLoad(image);
    }
  }, [image, onLoad]);

  return null; // Don't render here, just handle loading
};

// Optimized blur component with dynamic blur intensity support
const BlurComponent = ({ shapeProps, isSelected, onSelect, onChange, image, imageOffset, hideOutline = false, getDynamicStrokeWidth }) => {
  const shapeRef = useRef();
  const trRef = useRef();
  const blurImageRef = useRef();

  useEffect(() => {
    if (isSelected) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  // Apply blur effect when blur radius changes
  useEffect(() => {
    if (blurImageRef.current && image) {
      const node = blurImageRef.current;
      try {
        // Clear previous filters and cache
        node.clearCache();
        node.filters([]);
        
        // Apply new blur with current intensity
        const blurRadius = shapeProps.blurRadius || 10; // Default to 10px instead of 30px
        node.cache();
        node.filters([Konva.Filters.Blur]);
        node.blurRadius(blurRadius);
        node.getLayer()?.batchDraw();
        
        console.log('Blur applied with radius:', blurRadius);
      } catch (e) {
        console.error('Error applying blur:', e);
      }
    }
  }, [shapeProps.blurRadius, image]);

  console.log('BlurComponent rendering for shape:', shapeProps.type, 'blur radius:', shapeProps.blurRadius);

  return (
    <>
      {/* Clipped blurred image with dynamic blur intensity */}
      <Group
        clipFunc={(ctx) => {
          if (shapeProps.type === 'circle') {
            ctx.arc(shapeProps.x, shapeProps.y, shapeProps.radius, 0, Math.PI * 2, false);
          } else {
            ctx.rect(shapeProps.x, shapeProps.y, shapeProps.width, shapeProps.height);
          }
        }}
      >
        {/* Blurred image with adjustable intensity */}
        {image && (
          <Image
            ref={blurImageRef}
            image={image}
            x={imageOffset.x}
            y={imageOffset.y}
            width={image.width}
            height={image.height}
            listening={false}
          />
        )}
      </Group>
      
      {/* Border outline for the blur shape - hide during export */}
      {!hideOutline && (shapeProps.type === 'circle' ? (
        <Circle
          x={shapeProps.x}
          y={shapeProps.y}
          radius={shapeProps.radius}
          fill="transparent"
          stroke="rgba(255, 255, 255, 0.9)"
          strokeWidth={getDynamicStrokeWidth ? getDynamicStrokeWidth(3) : 3}
          strokeDashArray={[8, 4]}
          listening={false}
        />
      ) : (
        <Rect
          x={shapeProps.x}
          y={shapeProps.y}
          width={shapeProps.width}
          height={shapeProps.height}
          fill="transparent"
          stroke="rgba(255, 255, 255, 0.9)"
          strokeWidth={getDynamicStrokeWidth ? getDynamicStrokeWidth(3) : 3}
          strokeDashArray={[8, 4]}
          listening={false}
        />
      ))}
      
      {/* Invisible shape for interaction */}
      {shapeProps.type === 'circle' ? (
        <Circle
          onClick={onSelect}
          onTap={onSelect}
          ref={shapeRef}
          x={shapeProps.x}
          y={shapeProps.y}
          radius={shapeProps.radius}
          fill="transparent"
          stroke={isSelected ? '#007bff' : 'transparent'}
          strokeWidth={isSelected ? getDynamicStrokeWidth(2) : 0}
          strokeDashArray={isSelected ? [5, 5] : []}
          draggable
          onDragEnd={(e) => {
            onChange({
              ...shapeProps,
              x: e.target.x(),
              y: e.target.y(),
            });
          }}
          onTransformEnd={(e) => {
            const node = shapeRef.current;
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
            
            node.scaleX(1);
            node.scaleY(1);
            
            onChange({
              ...shapeProps,
              x: node.x(),
              y: node.y(),
              radius: Math.max(5, node.radius() * scaleX),
            });
          }}
        />
      ) : (
        <Rect
          onClick={onSelect}
          onTap={onSelect}
          ref={shapeRef}
          x={shapeProps.x}
          y={shapeProps.y}
          width={shapeProps.width}
          height={shapeProps.height}
          fill="transparent"
          stroke={isSelected ? '#007bff' : 'transparent'}
          strokeWidth={isSelected ? getDynamicStrokeWidth(2) : 0}
          strokeDashArray={isSelected ? [5, 5] : []}
          draggable
          onDragEnd={(e) => {
            onChange({
              ...shapeProps,
              x: e.target.x(),
              y: e.target.y(),
            });
          }}
          onTransformEnd={(e) => {
            const node = shapeRef.current;
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
            
            node.scaleX(1);
            node.scaleY(1);
            
            onChange({
              ...shapeProps,
              x: node.x(),
              y: node.y(),
              width: Math.max(5, node.width() * scaleX),
              height: Math.max(5, node.height() * scaleY),
            });
          }}
        />
      )}
      
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            return newBox.width < 5 || newBox.height < 5 ? oldBox : newBox;
          }}
          anchorSize={16}
          anchorStroke="#007bff"
          anchorStrokeWidth={2}
          anchorFill="white"
        />
      )}
    </>
  );
};

// Crop transformer component
const CropTransformer = ({ cropArea, onTransform, transformerRef, getDynamicStrokeWidth }) => {
  const cropRectRef = useRef();

  useEffect(() => {
    if (transformerRef.current && cropRectRef.current) {
      transformerRef.current.nodes([cropRectRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [cropArea, transformerRef]);

  return (
    <>
      <Rect
        ref={cropRectRef}
        x={cropArea.x}
        y={cropArea.y}
        width={cropArea.width}
        height={cropArea.height}
        fill="rgba(0, 123, 255, 0.1)"
        stroke="#007bff"
        strokeWidth={getDynamicStrokeWidth ? getDynamicStrokeWidth(2) : 2}
        dash={[5, 5]}
        draggable
        onDragEnd={(e) => {
          const newArea = {
            x: e.target.x(),
            y: e.target.y(),
            width: cropArea.width,
            height: cropArea.height,
          };
          onTransform(newArea);
        }}
        onTransformEnd={(e) => {
          const node = cropRectRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          
          // Reset scale
          node.scaleX(1);
          node.scaleY(1);
          
          const newArea = {
            x: Math.max(0, node.x()),
            y: Math.max(0, node.y()),
            width: Math.max(50, node.width() * scaleX),
            height: Math.max(50, node.height() * scaleY),
          };
          onTransform(newArea);
        }}
      />
      <Transformer
        ref={transformerRef}
        boundBoxFunc={(oldBox, newBox) => {
          // Constrain minimum size
          if (newBox.width < 50 || newBox.height < 50) {
            return oldBox;
          }
          return newBox;
        }}
        enabledAnchors={[
          'top-left', 'top-center', 'top-right',
          'middle-left', 'middle-right',
          'bottom-left', 'bottom-center', 'bottom-right'
        ]}
        anchorSize={16}
        anchorStroke="#007bff"
        anchorStrokeWidth={2}
        anchorFill="white"
      />
    </>
  );
};

// Individual shape components
const CircleComponent = ({ shapeProps, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Circle
        onClick={onSelect}
        onTap={onSelect}
        ref={shapeRef}
        {...shapeProps}
        draggable
        onDragEnd={(e) => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y(),
          });
        }}
        onTransformEnd={(e) => {
          const node = shapeRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          
          // Reset the scale to 1 after applying it to the radius
          node.scaleX(1);
          node.scaleY(1);
          
          onChange({
            ...shapeProps,
            x: node.x(),
            y: node.y(),
            radius: Math.max(5, node.radius() * scaleX),
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            return newBox.width < 5 || newBox.height < 5 ? oldBox : newBox;
          }}
          anchorSize={16}
          anchorStroke="#007bff"
          anchorStrokeWidth={2}
          anchorFill="white"
        />
      )}
    </>
  );
};

const RectComponent = ({ shapeProps, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Rect
        onClick={onSelect}
        onTap={onSelect}
        ref={shapeRef}
        {...shapeProps}
        draggable
        onDragEnd={(e) => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y(),
          });
        }}
        onTransformEnd={(e) => {
          const node = shapeRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          
          // Reset the scale to 1 after applying it to the dimensions
          node.scaleX(1);
          node.scaleY(1);
          
          onChange({
            ...shapeProps,
            x: node.x(),
            y: node.y(),
            width: Math.max(5, node.width() * scaleX),
            height: Math.max(5, node.height() * scaleY),
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            return newBox.width < 5 || newBox.height < 5 ? oldBox : newBox;
          }}
          anchorSize={16}
          anchorStroke="#007bff"
          anchorStrokeWidth={2}
          anchorFill="white"
        />
      )}
    </>
  );
};

const ArrowComponent = ({ shapeProps, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Arrow
        onClick={onSelect}
        onTap={onSelect}
        ref={shapeRef}
        {...shapeProps}
        draggable
        onDragEnd={(e) => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y(),
          });
        }}
        onTransformEnd={(e) => {
          const node = shapeRef.current;
          onChange({
            ...shapeProps,
            x: node.x(),
            y: node.y(),
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          anchorSize={16}
          anchorStroke="#007bff"
          anchorStrokeWidth={2}
          anchorFill="white"
        />
      )}
    </>
  );
};

const TextComponent = ({ shapeProps, isSelected, onSelect, onChange, onDoubleClick }) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Text
        onClick={onSelect}
        onTap={onSelect}
        onDblClick={() => onDoubleClick && onDoubleClick(shapeProps)}
        ref={shapeRef}
        {...shapeProps}
        draggable
        onDragEnd={(e) => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y(),
          });
        }}
        onTransformEnd={(e) => {
          const node = shapeRef.current;
          const scaleX = node.scaleX();
          
          // Reset the scale to 1 after applying it to the font size
          node.scaleX(1);
          node.scaleY(1);
          
          onChange({
            ...shapeProps,
            x: node.x(),
            y: node.y(),
            fontSize: Math.max(5, shapeProps.fontSize * scaleX),
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          enabledAnchors={['middle-left', 'middle-right']}
          boundBoxFunc={(oldBox, newBox) => {
            return newBox.width < 5 || newBox.height < 5 ? oldBox : newBox;
          }}
          anchorSize={16}
          anchorStroke="#007bff"
          anchorStrokeWidth={2}
          anchorFill="white"
        />
      )}
    </>
  );
};

function FrameEditorPage() {
  const { projectId, itemId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [timelineItem, setTimelineItem] = useState(null);
  const [image, setImage] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tool, setTool] = useState('select');
  const [currentColor, setCurrentColor] = useState('#ff0000');
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentText, setCurrentText] = useState('Sample Text');
  const [fontSize, setFontSize] = useState(24);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [originalImageSize, setOriginalImageSize] = useState({ width: 0, height: 0 });
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [displayScale, setDisplayScale] = useState(1);
  const [isResizing, setIsResizing] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hideBlurOutlines, setHideBlurOutlines] = useState(false);
  // Text editing states
  const [editingTextId, setEditingTextId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [textInputPosition, setTextInputPosition] = useState({ x: 0, y: 0 });
  const stageRef = useRef();
  const cropTransformerRef = useRef();

  // Calculate dynamic stroke width based on image size (perfect at 1920x1080)
  const getDynamicStrokeWidth = useCallback((baseWidth = 4) => {
    const referenceWidth = 1920; // 1080p width where stroke looks perfect
    const currentWidth = canvasSize.width;
    const scale = currentWidth / referenceWidth;
    return Math.max(1, Math.round(baseWidth * scale));
  }, [canvasSize.width]);

  // Function to parse URL parameters and create shapes/text
  // Supports multiple formats:
  // 1. Single shape: ?shape={"type":"circle","x":100,"y":100}
  // 2. Multiple shapes: ?shapes=[{"type":"circle"},{"type":"rectangle"}]
  // 3. Single text: ?text={"text":"Hello","x":200,"y":200}
  // 4. Multiple texts: ?texts=[{"text":"Hello"},{"text":"World"}]
  const parseURLShapes = useCallback(() => {
    const shapesFromURL = [];
    let shapeCounter = 0;

    // Helper to create unique IDs
    const createUniqueId = (prefix = 'url') => {
      shapeCounter++;
      return `${Date.now()}_${prefix}_${shapeCounter}`;
    };

    // Helper to apply defaults to shape data
    const applyShapeDefaults = (shapeData) => {
      // Check if coordinates are relative (0-1) and convert to actual image coordinates
      let actualX, actualY, actualWidth, actualHeight, actualRadius, actualFontSize;

      if (shapeData.coordinateSystem === 'relative') {
        // Convert relative coordinates (0-1) to actual image coordinates
        actualX = (shapeData.x || 0.5) * canvasSize.width;
        actualY = (shapeData.y || 0.5) * canvasSize.height;
        actualWidth = (shapeData.width || 0.1) * canvasSize.width;
        actualHeight = (shapeData.height || 0.1) * canvasSize.height;
        actualRadius = (shapeData.radius || 0.05) * canvasSize.width; // Use width for radius scale
        actualFontSize = (shapeData.fontSize || 0.03) * canvasSize.height; // Use height for font scale

        console.log('Converting relative coordinates for', shapeData.type, ':', {
          relativeX: shapeData.x, actualX,
          relativeY: shapeData.y, actualY,
          canvasSize,
          actualFontSize,
          calculation: `${shapeData.x} * ${canvasSize.width} = ${actualX}`
        });
      } else if (shapeData.coordinateSystem === 'absolute') {
        // Use absolute coordinates and sizes directly from URL
        actualX = shapeData.x || canvasSize.width / 2;
        actualY = shapeData.y || canvasSize.height / 2;
        // Use the exact sizes passed from ClipEditor
        actualWidth = shapeData.width || 120;
        actualHeight = shapeData.height || 80;
        actualRadius = shapeData.radius || 30;
        actualFontSize = shapeData.fontSize || 24;

        console.log('Using absolute coordinates and sizes for', shapeData.type, ':', {
          originalX: shapeData.x, actualX,
          originalY: shapeData.y, actualY,
          originalWidth: shapeData.width, actualWidth,
          originalHeight: shapeData.height, actualHeight,
          radius: shapeData.radius,
          canvasSize,
          allShapeData: shapeData,
          calculation: `Using coordinates and sizes directly from URL`
        });
      } else {
        // Fallback: Use coordinates as-is
        actualX = shapeData.x || canvasSize.width / 2;
        actualY = shapeData.y || canvasSize.height / 2;
        actualWidth = shapeData.width || 120;
        actualHeight = shapeData.height || 80;
        actualRadius = shapeData.radius || 30;
        actualFontSize = shapeData.fontSize || 24;
      }

      const baseDefaults = {
        x: actualX,
        y: actualY,
        fill: shapeData.fill || 'transparent',
        stroke: shapeData.stroke || '#ff0000',
        strokeWidth: shapeData.strokeWidth || getDynamicStrokeWidth(4),
      };

      // Type-specific defaults using calculated actual values
      switch (shapeData.type) {
        case 'circle':
          return {
            ...baseDefaults,
            ...shapeData, // shapeData comes last to preserve URL sizes
            // For absolute coordinates, don't override sizes - use what's in shapeData
            // Override coordinates only
            x: actualX,
            y: actualY
          };
        case 'rectangle':
          return {
            ...baseDefaults,
            ...shapeData, // shapeData comes last to preserve URL sizes
            // For absolute coordinates, don't override sizes - use what's in shapeData
            // Override coordinates only
            x: actualX,
            y: actualY
          };
        case 'arrow':
          // Ensure arrow uses native Frame Editor format
          // For arrows, if fill is transparent, use a visible color for both fill and stroke
          let arrowColor = baseDefaults.stroke; // Default red color
          if (shapeData.fill && shapeData.fill !== 'transparent') {
            arrowColor = shapeData.fill;
          } else if (shapeData.stroke && shapeData.stroke !== 'transparent') {
            arrowColor = shapeData.stroke;
          }

          const arrowData = {
            type: 'arrow',
            // Only include properties that Konva Arrow component expects
            points: shapeData.points || [
              actualX, actualY,
              actualX + (shapeData.width || 120), actualY + (shapeData.height || 60)
            ],
            fill: arrowColor,  // Use visible color for fill
            stroke: arrowColor, // Use same color for stroke
            strokeWidth: shapeData.strokeWidth || getDynamicStrokeWidth(5),
            pointerLength: shapeData.pointerLength || 15,
            pointerWidth: shapeData.pointerWidth || 12
            // Don't include x,y as arrows use points for positioning
          };
          console.log('Created native arrow with color:', arrowColor, arrowData);
          console.log('Arrow points:', arrowData.points);
          return arrowData;
        case 'text':
          return {
            ...shapeData,
            text: shapeData.text || 'Sample Text',
            fontSize: actualFontSize,
            fontFamily: shapeData.fontFamily || 'Arial',
            fontStyle: shapeData.fontStyle || 'bold',
            fill: '#ff0000', // Always use red for text
            // Ensure text has no stroke/outline
            stroke: undefined,
            strokeWidth: 0,
            // Override coordinates and fontSize
            x: actualX,
            y: actualY
          };
        default:
          return {
            ...shapeData,
            ...baseDefaults,
            // Override coordinates
            x: actualX,
            y: actualY
          };
      }
    };

    // Parse multiple shapes parameter (only use plural form to avoid duplicates)
    const shapesParam = searchParams.get('shapes');
    if (shapesParam) {
      try {
        const shapesArray = JSON.parse(decodeURIComponent(shapesParam));
        console.log('=== FRAME EDITOR SHAPE PARSING ===');
        console.log('Total shapes in URL:', shapesArray.length);
        if (Array.isArray(shapesArray)) {
          shapesArray.forEach((shapeData, index) => {
            console.log(`Processing shape ${index}:`, shapeData.type, 'Data:', shapeData);
            if (shapeData.type) {
              const shapeId = createUniqueId(`shapes_${index}`);
              console.log(`Creating shape ${index} with ID:`, shapeId);
              const shape = {
                id: shapeId,
                ...applyShapeDefaults(shapeData)
              };
              console.log(`Final shape ${index}:`, shape);
              shapesFromURL.push(shape);
            }
          });
        }
      } catch (error) {
        console.error('Error parsing shapes parameter:', error);
      }
    }

    // Parse multiple texts parameter (only use plural form to avoid duplicates)
    const textsParam = searchParams.get('texts');
    if (textsParam) {
      try {
        const textsArray = JSON.parse(decodeURIComponent(textsParam));
        if (Array.isArray(textsArray)) {
          textsArray.forEach((textData, index) => {
            const textShape = {
              id: createUniqueId(`texts_${index}`),
              type: 'text',
              ...applyShapeDefaults({ ...textData, type: 'text' })
            };
            shapesFromURL.push(textShape);
          });
        }
      } catch (error) {
        console.error('Error parsing texts parameter:', error);
      }
    }

    return shapesFromURL;
  }, [searchParams, canvasSize, getDynamicStrokeWidth]);

  // Initialize shapes from URL parameters (only once)
  const [urlShapesLoaded, setUrlShapesLoaded] = useState(false);

  useEffect(() => {
    console.log('=== URL SHAPES LOADING EFFECT ===');
    console.log('Canvas size:', canvasSize);
    console.log('URL shapes loaded:', urlShapesLoaded);
    console.log('Current shapes count:', shapes.length);

    if (canvasSize.width > 0 && canvasSize.height > 0 && !urlShapesLoaded) {
      console.log('Loading URL shapes...');
      const urlShapes = parseURLShapes();
      if (urlShapes.length > 0) {
        console.log('Adding URL shapes to canvas:', urlShapes.length, 'shapes');
        setShapes(prevShapes => {
          console.log('Previous shapes:', prevShapes.length);
          console.log('Adding shapes:', urlShapes.length);
          return [...prevShapes, ...urlShapes];
        });
        setUrlShapesLoaded(true);
      } else {
        console.log('No URL shapes to load');
        setUrlShapesLoaded(true);
      }
    }
  }, [canvasSize.width, canvasSize.height, urlShapesLoaded]); // Removed parseURLShapes dependency

  const colorPresets = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffffff', '#000000'];

  // Fetch timeline item data
  useEffect(() => {
    const fetchTimelineItem = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/timeline-items/${itemId}`);
        if (response.ok) {
          const data = await response.json();
          setTimelineItem(data);
        } else {
          console.error('Failed to fetch timeline item');
          navigate(`/project/${projectId}`);
        }
      } catch (error) {
        console.error('Error fetching timeline item:', error);
        navigate(`/project/${projectId}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (itemId) {
      fetchTimelineItem();
    }
  }, [itemId, projectId, navigate]);

  // Keyboard shortcuts for layer movement
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle keyboard shortcuts when not typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      if (e.key === ']') {
        e.preventDefault();
        moveLayerUp();
      } else if (e.key === '[') {
        e.preventDefault();
        moveLayerDown();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelected();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, shapes]); // Include dependencies for the functions

  const imageUrl = timelineItem?.screenshot_path 
    ? `${API_URL}${timelineItem.screenshot_path}`
    : null;
    
  console.log('Timeline item:', timelineItem);
  console.log('Image URL:', imageUrl);

  const handleImageLoad = useCallback((img) => {
    console.log('Image loaded:', img.width, 'x', img.height);
    console.log('Image natural size:', img.naturalWidth, 'x', img.naturalHeight);
    setImage(img);
    // Store original image dimensions
    setOriginalImageSize({ width: img.width, height: img.height });
    // Set canvas to EXACT image size (this maintains the aspect ratio)
    setCanvasSize({ width: img.width, height: img.height });
    setStageSize({ width: img.width, height: img.height });
    
    // Calculate display scale to fit the canvas in available screen width
    const availableWidth = window.innerWidth - 100; // Account for padding
    const availableHeight = window.innerHeight - 300; // Account for header and toolbar
    
    // Calculate scale to fit either width or height (whichever is more constraining)
    const scaleX = availableWidth / img.width;
    const scaleY = availableHeight / img.height;
    const scale = Math.min(scaleX, scaleY, 1); // Never scale up, only down
    
    console.log('Available space:', availableWidth, 'x', availableHeight);
    console.log('Display scale:', scale, 'for image:', img.width, 'x', img.height);
    console.log('Final display size:', Math.round(img.width * scale), 'x', Math.round(img.height * scale));
    
    setDisplayScale(scale);
  }, []);


  const checkDeselect = (e) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedId(null);
    }
  };

  const handleStageMouseDown = (e) => {
    // Check if we clicked on empty space (not on a shape)
    const clickedOnEmpty = e.target === e.target.getStage() || e.target.getType() === 'Stage';
    if (clickedOnEmpty) {
      setSelectedId(null);
    }
  };

  const handleShapeChange = (id, newAttrs) => {
    setShapes(shapes.map(shape => 
      shape.id === id ? { ...shape, ...newAttrs } : shape
    ));
  };

  const deleteSelected = () => {
    if (selectedId) {
      setShapes(shapes.filter(shape => shape.id !== selectedId));
      setSelectedId(null);
    }
  };

  const duplicateSelected = () => {
    if (selectedId) {
      const selectedShape = shapes.find(shape => shape.id === selectedId);
      if (selectedShape) {
        const newShape = {
          ...selectedShape,
          id: Date.now().toString(),
          x: selectedShape.x + 20,
          y: selectedShape.y + 20,
        };
        setShapes([...shapes, newShape]);
        setSelectedId(newShape.id);
      }
    }
  };

  const moveLayerUp = () => {
    if (selectedId) {
      const currentIndex = shapes.findIndex(shape => shape.id === selectedId);
      if (currentIndex < shapes.length - 1) {
        const newShapes = [...shapes];
        [newShapes[currentIndex], newShapes[currentIndex + 1]] = [newShapes[currentIndex + 1], newShapes[currentIndex]];
        setShapes(newShapes);
      }
    }
  };

  const moveLayerDown = () => {
    if (selectedId) {
      const currentIndex = shapes.findIndex(shape => shape.id === selectedId);
      if (currentIndex > 0) {
        const newShapes = [...shapes];
        [newShapes[currentIndex], newShapes[currentIndex - 1]] = [newShapes[currentIndex - 1], newShapes[currentIndex]];
        setShapes(newShapes);
      }
    }
  };

  const startTextEditing = (textShape) => {
    const stageBox = stageRef.current.container().getBoundingClientRect();
    const textPosition = {
      x: stageBox.left + (textShape.x + imageOffset.x) * displayScale,
      y: stageBox.top + (textShape.y + imageOffset.y) * displayScale
    };
    
    setEditingTextId(textShape.id);
    setEditingText(textShape.text);
    setTextInputPosition(textPosition);
    setSelectedId(null); // Deselect while editing
  };

  const saveTextEdit = () => {
    if (editingTextId && editingText.trim()) {
      handleShapeChange(editingTextId, { text: editingText.trim() });
    }
    setEditingTextId(null);
    setEditingText('');
  };

  const cancelTextEdit = () => {
    setEditingTextId(null);
    setEditingText('');
  };

  const handleTextEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveTextEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelTextEdit();
    }
  };

  const handleCanvasResize = (width, height) => {
    const newWidth = Math.max(100, Math.min(width, originalImageSize.width));
    const newHeight = Math.max(100, Math.min(height, originalImageSize.height));
    setCanvasSize({ width: newWidth, height: newHeight });
    setStageSize({ width: newWidth, height: newHeight });
    
    // Recalculate display scale to fit in available screen space
    const availableWidth = window.innerWidth - 100;
    const availableHeight = window.innerHeight - 300;
    const scaleX = availableWidth / newWidth;
    const scaleY = availableHeight / newHeight;
    const scale = Math.min(scaleX, scaleY, 1);
    setDisplayScale(scale);
  };

  const resetToOriginalSize = () => {
    setCanvasSize({ width: originalImageSize.width, height: originalImageSize.height });
    setStageSize({ width: originalImageSize.width, height: originalImageSize.height });
    
    // Recalculate display scale for original size
    const availableWidth = window.innerWidth - 100;
    const availableHeight = window.innerHeight - 300;
    const scaleX = availableWidth / originalImageSize.width;
    const scaleY = availableHeight / originalImageSize.height;
    const scale = Math.min(scaleX, scaleY, 1);
    setDisplayScale(scale);
  };

  const handleZoomChange = (newZoom) => {
    const clampedZoom = Math.max(0.1, Math.min(3, newZoom));
    setDisplayScale(clampedZoom);
  };

  const handleCropToggle = () => {
    if (isCropping) {
      // Cancel crop mode
      setIsCropping(false);
      setCropArea({ x: 0, y: 0, width: 0, height: 0 });
      setTool('select');
    } else {
      // Enter crop mode
      setIsCropping(true);
      setTool('crop');
      // Initialize crop area to full canvas
      setCropArea({ 
        x: 0, 
        y: 0, 
        width: canvasSize.width, 
        height: canvasSize.height 
      });
    }
  };

  const handleCropTransform = (cropRect) => {
    setCropArea(cropRect);
  };

  const applyCrop = () => {
    if (cropArea.width > 0 && cropArea.height > 0) {
      // Ensure integer dimensions to avoid sub-pixel issues
      const croppedWidth = Math.round(cropArea.width);
      const croppedHeight = Math.round(cropArea.height);
      
      // Update canvas size to crop area dimensions
      setCanvasSize({ width: croppedWidth, height: croppedHeight });
      setStageSize({ width: croppedWidth, height: croppedHeight });
      
      // Set image offset to show only the cropped area with rounded values
      setImageOffset({ 
        x: -Math.round(cropArea.x), 
        y: -Math.round(cropArea.y) 
      });
      
      // Adjust all existing shapes to account for the crop offset with rounded values
      const cropX = Math.round(cropArea.x);
      const cropY = Math.round(cropArea.y);
      
      const adjustedShapes = shapes.map(shape => ({
        ...shape,
        x: shape.x - cropX,
        y: shape.y - cropY,
        // For arrows, adjust points array
        ...(shape.points && {
          points: shape.points.map((point, index) => 
            index % 2 === 0 ? point - cropX : point - cropY
          )
        })
      })).filter(shape => {
        // Only keep shapes that are at least partially visible in the crop area
        if (shape.points) {
          // For arrows, check if any point is within the crop area
          for (let i = 0; i < shape.points.length; i += 2) {
            const x = shape.points[i] + cropX;
            const y = shape.points[i + 1] + cropY;
            if (x >= 0 && x <= croppedWidth && y >= 0 && y <= croppedHeight) {
              return true;
            }
          }
          return false;
        } else {
          // For other shapes, check if they're within the crop area
          return shape.x + cropX >= -50 && shape.x + cropX <= croppedWidth + 50 &&
                 shape.y + cropY >= -50 && shape.y + cropY <= croppedHeight + 50;
        }
      });
      
      setShapes(adjustedShapes);
      setSelectedId(null);
      
      // Reset crop mode
      setIsCropping(false);
      setCropArea({ x: 0, y: 0, width: 0, height: 0 });
      setTool('select');
      
      // Recalculate display scale using the rounded crop dimensions
      const availableWidth = window.innerWidth - 100;
      const availableHeight = window.innerHeight - 300;
      const scaleX = availableWidth / croppedWidth;
      const scaleY = availableHeight / croppedHeight;
      const scale = Math.min(scaleX, scaleY, 1);
      setDisplayScale(scale);
    }
  };

  const saveAnnotatedImage = async () => {
    if (!stageRef.current || !timelineItem) return;
    
    try {
      setIsSaving(true);
      
      // Deselect any selected objects before saving
      setSelectedId(null);
      
      // Hide blur outlines for clean export
      setHideBlurOutlines(true);
      
      // Wait for a short delay to ensure deselection and outline hiding is rendered
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Get canvas data as base64 with precise dimensions
      // Force a re-render to ensure everything is properly drawn
      try {
        // Get all layers and redraw them
        const layers = stageRef.current.getLayers();
        layers.forEach(layer => {
          if (layer && layer.batchDraw) {
            layer.batchDraw();
          }
        });
        
        // Also try the stage-level draw as fallback
        if (stageRef.current.draw) {
          stageRef.current.draw();
        }
      } catch (drawError) {
        console.warn('Could not force redraw before export:', drawError);
      }
      
      console.log('Canvas size for export:', canvasSize);
      
      // Calculate if we need to resize for web optimization (max 1600px width)
      let exportPixelRatio = 1;
      let finalExportWidth = canvasSize.width;
      let finalExportHeight = canvasSize.height;
      
      if (canvasSize.width > 1600) {
        exportPixelRatio = 1600 / canvasSize.width;
        finalExportWidth = 1600;
        finalExportHeight = Math.round(canvasSize.height * exportPixelRatio);
        console.log(`🔄 RESIZING: Canvas ${canvasSize.width}x${canvasSize.height} → Export ${finalExportWidth}x${finalExportHeight} (ratio: ${exportPixelRatio.toFixed(3)})`);
      } else {
        console.log(`✅ NO RESIZE NEEDED: Canvas ${canvasSize.width}x${canvasSize.height} ≤ 1600px - saving at original size`);
      }
      
      const dataURL = stageRef.current.toDataURL({
        mimeType: 'image/png',
        quality: 1,
        pixelRatio: exportPixelRatio,
      });
      
      console.log(`📊 Final exported dimensions: ${finalExportWidth}x${finalExportHeight}`);
      
      console.log('Generated dataURL length:', dataURL.length);
      
      // Send to backend
      const response = await fetch(`${API_URL}/timeline-items/${timelineItem.id}/save-annotated-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_data: dataURL
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Save error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Annotated image saved successfully:', result);
      
      // Show success message
      alert('Annotated frame saved successfully!');
      
    } catch (error) {
      console.error('Error saving annotated image:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        timelineItemId: timelineItem?.id,
        canvasSize: canvasSize
      });
      alert(`Failed to save annotated frame: ${error.message}`);
    } finally {
      setIsSaving(false);
      // Restore blur outlines after saving
      setHideBlurOutlines(false);
    }
  };

  const handleBackToEditor = () => {
    navigate(`/project/${projectId}`);
  };

  if (isLoading) {
    return (
      <div className="frame-editor-page">
        <div className="loading-canvas">
          <div className="loading-spinner"></div>
          <p>Loading frame editor...</p>
        </div>
      </div>
    );
  }

  if (!timelineItem) {
    return (
      <div className="frame-editor-page">
        <div className="loading-canvas">
          <p>Frame not found</p>
          <button onClick={handleBackToEditor} className="back-button">
            Back to Editor
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="frame-editor-page">
      {/* Header */}
      <header className="frame-editor-header">
        <h1 className="frame-editor-title">
          <div className="icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
          </div>
          Frame Annotation Editor
          <span style={{ fontSize: '0.6em', color: '#6c757d' }}>
            {timelineItem.type === 'frame' ? `Frame at ${timelineItem.timestamp?.toFixed(2)}s` : 'Clip Screenshot'}
          </span>
        </h1>
        <div className="header-actions">
          <button onClick={handleBackToEditor} className="back-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            Back to Editor
          </button>
          <button onClick={saveAnnotatedImage} disabled={isSaving} className="save-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
            </svg>
            {isSaving ? 'Saving...' : 'Save Frame'}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="frame-editor-main">
        {/* Toolbar */}
        <div className="frame-editor-toolbar">
          <div className="toolbar-section">
            <span className="toolbar-section-title">Tools</span>
            <div className="tool-group">
              <button 
                className={`tool-button ${tool === 'select' ? 'active' : ''}`}
                onClick={() => setTool('select')}
                data-tooltip="Select and move shapes"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                Select
              </button>
              <button 
                className={`tool-button ${tool === 'circle' ? 'active' : ''}`}
                onClick={() => {
                  // Create a circle in the center of the canvas
                  const centerX = canvasSize.width / 2;
                  const centerY = canvasSize.height / 2;
                  const id = Date.now().toString();
                  
                  const newCircle = {
                    id,
                    type: 'circle',
                    x: centerX,
                    y: centerY,
                    radius: 30,
                    fill: 'transparent',
                    stroke: currentColor,
                    strokeWidth: getDynamicStrokeWidth(4),
                  };
                  
                  setShapes([...shapes, newCircle]);
                  setSelectedId(id);
                  setTool('select'); // Switch back to select mode
                }}
                data-tooltip="Draw circles"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" strokeWidth="2"/>
                </svg>
                Circle
              </button>
              <button 
                className={`tool-button ${tool === 'rectangle' ? 'active' : ''}`}
                onClick={() => {
                  // Create a rectangle in the center of the canvas
                  const centerX = canvasSize.width / 2 - 60; // Half width
                  const centerY = canvasSize.height / 2 - 40; // Half height
                  const id = Date.now().toString();
                  
                  const newRectangle = {
                    id,
                    type: 'rectangle',
                    x: centerX,
                    y: centerY,
                    width: 120,
                    height: 80,
                    fill: 'transparent',
                    stroke: currentColor,
                    strokeWidth: getDynamicStrokeWidth(4),
                  };
                  
                  setShapes([...shapes, newRectangle]);
                  setSelectedId(id);
                  setTool('select'); // Switch back to select mode
                }}
                data-tooltip="Draw rectangles"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="3" y="3" width="18" height="18" stroke="currentColor" fill="none" strokeWidth="2"/>
                </svg>
                Rectangle
              </button>
              <button 
                className={`tool-button ${tool === 'arrow' ? 'active' : ''}`}
                onClick={() => {
                  // Create an arrow in the center of the canvas
                  const centerX = canvasSize.width / 2 - 60; // Start point
                  const centerY = canvasSize.height / 2 - 30; // Start point
                  const id = Date.now().toString();
                  
                  const newArrow = {
                    id,
                    type: 'arrow',
                    points: [centerX, centerY, centerX + 120, centerY + 60],
                    fill: currentColor,
                    stroke: currentColor,
                    strokeWidth: getDynamicStrokeWidth(5),
                    pointerLength: 15,
                    pointerWidth: 12,
                  };
                  
                  setShapes([...shapes, newArrow]);
                  setSelectedId(id);
                  setTool('select'); // Switch back to select mode
                }}
                data-tooltip="Draw arrows"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M1.5 12L12 2.5L22.5 12L12 21.5L1.5 12Z" stroke="currentColor" fill="none" strokeWidth="2"/>
                </svg>
                Arrow
              </button>
              <button 
                className={`tool-button ${tool === 'text' ? 'active' : ''}`}
                onClick={() => {
                  // Create text in the center of the canvas
                  const centerX = canvasSize.width / 2 - 50; // Approximate text center
                  const centerY = canvasSize.height / 2;
                  const id = Date.now().toString();
                  
                  const newText = {
                    id,
                    type: 'text',
                    x: centerX,
                    y: centerY,
                    text: currentText,
                    fontSize: fontSize,
                    fontFamily: 'Arial',
                    fill: currentColor,
                    fontStyle: 'bold',
                  };
                  
                  setShapes([...shapes, newText]);
                  setSelectedId(id);
                  setTool('select'); // Switch back to select mode
                }}
                data-tooltip="Add text"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 4v3h5.5v12h3V7H19V4z"/>
                </svg>
                Text
              </button>
              <button 
                className={`tool-button ${isCropping ? 'active' : ''}`}
                onClick={handleCropToggle}
                data-tooltip="Crop image"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 15h2V7c0-1.1-.9-2-2-2H9v2h8v8zM7 17V1H5v4H1v2h4v10c0 1.1.9 2 2 2h10v4h2v-4h4v-2H7z"/>
                </svg>
                Crop
              </button>
            </div>
          </div>


          {tool === 'text' && (
            <div className="toolbar-section">
              <span className="toolbar-section-title">Text</span>
              <div className="text-controls">
                <input 
                  type="text" 
                  value={currentText} 
                  onChange={(e) => setCurrentText(e.target.value)}
                  className="text-input"
                  placeholder="Enter text..."
                />
                <input 
                  type="number" 
                  value={fontSize} 
                  onChange={(e) => setFontSize(parseInt(e.target.value) || 24)}
                  className="font-size-input"
                  min="10"
                  max="72"
                  placeholder="Size"
                />
                <div className="color-control">
                  <label>Color:</label>
                  <input 
                    type="color" 
                    value={currentColor} 
                    onChange={(e) => setCurrentColor(e.target.value)}
                    className="style-color-picker"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Shape-specific controls when a shape is selected */}
          {selectedId && (
            (() => {
              const selectedShape = shapes.find(shape => shape.id === selectedId);
              if (selectedShape && (selectedShape.type === 'circle' || selectedShape.type === 'rectangle' || selectedShape.type === 'blur')) {
                
                // Determine current mode based on shape properties
                let currentMode = 'outline';
                if (selectedShape.isBlurMode) {
                  currentMode = 'blur';
                } else if (selectedShape.fill && selectedShape.fill !== 'transparent') {
                  currentMode = 'fill';
                }
                
                return (
                  <div className="toolbar-section">
                    <span className="toolbar-section-title">Shape Style</span>
                    <div className="shape-style-controls">
                      {/* Radio button modes */}
                      <div className="style-mode-controls">
                        <div className="mode-option">
                          <input 
                            type="radio" 
                            id="outline-mode" 
                            name="shape-mode"
                            checked={currentMode === 'outline'}
                            onChange={() => {
                              handleShapeChange(selectedId, { 
                                fill: 'transparent',
                                stroke: currentColor,
                                strokeWidth: getDynamicStrokeWidth(4),
                                isBlurMode: false,
                                filters: [],
                                blurRadius: 0
                              });
                            }}
                          />
                          <label htmlFor="outline-mode">Outline</label>
                        </div>
                        <div className="mode-option">
                          <input 
                            type="radio" 
                            id="fill-mode" 
                            name="shape-mode"
                            checked={currentMode === 'fill'}
                            onChange={() => {
                              handleShapeChange(selectedId, { 
                                fill: currentColor,
                                stroke: 'transparent',
                                strokeWidth: 0,
                                isBlurMode: false,
                                filters: [],
                                blurRadius: 0
                              });
                            }}
                          />
                          <label htmlFor="fill-mode">Fill</label>
                        </div>
                        <div className="mode-option">
                          <input 
                            type="radio" 
                            id="blur-mode" 
                            name="shape-mode"
                            checked={currentMode === 'blur'}
                            onChange={() => {
                              console.log('Switching to blur mode for shape:', selectedId);
                              handleShapeChange(selectedId, { 
                                fill: 'transparent',
                                stroke: 'transparent',
                                strokeWidth: 0,
                                isBlurMode: true,
                                blurRadius: 40 // Increased default blur for better visibility
                              });
                            }}
                          />
                          <label htmlFor="blur-mode">Blur</label>
                        </div>
                      </div>
                      
                      {/* Color picker for current mode */}
                      {(currentMode === 'outline' || currentMode === 'fill') && (
                        <div className="color-control">
                          <label>Color:</label>
                          <input 
                            type="color" 
                            value={currentMode === 'outline' ? (selectedShape.stroke || currentColor) : (selectedShape.fill || currentColor)} 
                            onChange={(e) => {
                              const newColor = e.target.value;
                              if (currentMode === 'outline') {
                                handleShapeChange(selectedId, { stroke: newColor });
                              } else {
                                handleShapeChange(selectedId, { fill: newColor });
                              }
                            }}
                            className="style-color-picker"
                          />
                        </div>
                      )}
                      
                      {/* Blur intensity for blur mode */}
                      {currentMode === 'blur' && (
                        <div className="blur-control">
                          <label>Blur Intensity:</label>
                          <input 
                            type="range" 
                            min="10" 
                            max="50" 
                            value={selectedShape.blurRadius || 25} 
                            onChange={(e) => {
                              const newBlurRadius = parseInt(e.target.value);
                              console.log('Updating blur radius to:', newBlurRadius);
                              handleShapeChange(selectedId, { 
                                blurRadius: newBlurRadius
                              });
                            }}
                            className="blur-slider"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              
              // Add text controls when text is selected
              if (selectedShape && selectedShape.type === 'text') {
                return (
                  <div className="toolbar-section">
                    <span className="toolbar-section-title">Text Style</span>
                    <div className="shape-style-controls">
                      <div className="color-control">
                        <label>Color:</label>
                        <input 
                          type="color" 
                          value={selectedShape.fill || currentColor} 
                          onChange={(e) => {
                            const newColor = e.target.value;
                            handleShapeChange(selectedId, { fill: newColor });
                          }}
                          className="style-color-picker"
                        />
                      </div>
                      <div className="style-control">
                        <label>Size:</label>
                        <input 
                          type="number" 
                          value={selectedShape.fontSize || 24} 
                          onChange={(e) => {
                            const newSize = parseInt(e.target.value) || 24;
                            handleShapeChange(selectedId, { fontSize: newSize });
                          }}
                          className="font-size-input"
                          min="10"
                          max="72"
                        />
                      </div>
                    </div>
                  </div>
                );
              }
              
              return null;
            })()
          )}


          <div className="toolbar-section">
            <span className="toolbar-section-title">Actions</span>
            <div className="action-controls">
              {isCropping ? (
                <>
                  <button onClick={applyCrop} className="action-button success">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    Apply Crop
                  </button>
                  <button onClick={handleCropToggle} className="action-button">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button onClick={moveLayerUp} disabled={!selectedId || shapes.findIndex(s => s.id === selectedId) === shapes.length - 1} className="action-button" title="Move layer up (])">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
                    </svg>
                    Layer Up
                  </button>
                  <button onClick={moveLayerDown} disabled={!selectedId || shapes.findIndex(s => s.id === selectedId) === 0} className="action-button" title="Move layer down ([)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 8.84L12 13.42l4.59-4.58L18 10l-6 6-6-6z"/>
                    </svg>
                    Layer Down
                  </button>
                  <button onClick={duplicateSelected} disabled={!selectedId} className="action-button success">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                    Duplicate
                  </button>
                  <button onClick={deleteSelected} disabled={!selectedId} className="action-button danger">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Canvas Container */}
        <div className="canvas-container">
          <div className="canvas-wrapper" style={{ transform: `scale(${displayScale})`, transformOrigin: 'top left' }}>
            {/* Hidden image loader */}
            {imageUrl && (
              <ImageComponent 
                src={imageUrl} 
                onLoad={handleImageLoad}
              />
            )}
            
            <Stage
              width={canvasSize.width}
              height={canvasSize.height}
              onMouseDown={handleStageMouseDown}
              onTouchStart={handleStageMouseDown}
              ref={stageRef}
            >
              <Layer>
                {image && (
                  <Image
                    image={image}
                    x={imageOffset.x}
                    y={imageOffset.y}
                    width={image.width}
                    height={image.height}
                  />
                )}
                
                {shapes.map((shape) => {
                  const isSelected = shape.id === selectedId;
                  const commonProps = {
                    key: shape.id,
                    shapeProps: shape,
                    isSelected,
                    onSelect: () => setSelectedId(shape.id),
                    onChange: (newAttrs) => handleShapeChange(shape.id, newAttrs),
                  };
                  
                  // Use BlurComponent for shapes in blur mode
                  if (shape.isBlurMode) {
                    console.log('Rendering blur shape:', shape.id, shape.type, shape.isBlurMode);
                    return (
                      <BlurComponent 
                        {...commonProps} 
                        image={image} 
                        imageOffset={imageOffset}
                        hideOutline={hideBlurOutlines}
                        getDynamicStrokeWidth={getDynamicStrokeWidth}
                      />
                    );
                  }
                  
                  switch (shape.type) {
                    case 'circle':
                      return <CircleComponent {...commonProps} />;
                    case 'rectangle':
                    case 'blur':
                      return <RectComponent {...commonProps} />;
                    case 'arrow':
                      return <ArrowComponent {...commonProps} />;
                    case 'text':
                      return <TextComponent {...commonProps} onDoubleClick={startTextEditing} />;
                    default:
                      return null;
                  }
                })}
                
                {/* Crop Transformer */}
                {isCropping && cropArea.width > 0 && cropArea.height > 0 && (
                  <CropTransformer
                    cropArea={cropArea}
                    onTransform={handleCropTransform}
                    transformerRef={cropTransformerRef}
                    getDynamicStrokeWidth={getDynamicStrokeWidth}
                  />
                )}
              </Layer>
              
            </Stage>
          </div>
        </div>
      </main>

      {/* Status Bar */}
      <div className="status-bar">
        <div className="status-info">
          <span>Shapes: {shapes.length}</span>
          <span>Selected: {selectedId ? 'Yes' : 'None'}</span>
          <span>Tool: {tool}</span>
          <span>Canvas: {canvasSize.width} × {canvasSize.height}</span>
          <span>Export: {canvasSize.width > 1600 ? `1600 × ${Math.round(canvasSize.height * (1600 / canvasSize.width))} (resized)` : `${canvasSize.width} × ${canvasSize.height} (original)`}</span>
          <span>Original: {originalImageSize.width} × {originalImageSize.height}</span>
          <span>Stage: {stageSize.width} × {stageSize.height}</span>
          <span>Image: {image ? `${image.width} × ${image.height}` : 'Not loaded'}</span>
        </div>
        <div className="zoom-controls">
          <button onClick={() => handleZoomChange(displayScale - 0.1)} className="zoom-button">
            -
          </button>
          <span>{Math.round(displayScale * 100)}%</span>
          <button onClick={() => handleZoomChange(displayScale + 0.1)} className="zoom-button">
            +
          </button>
          <button onClick={() => {
            // Reset to fit-to-window scale
            const availableWidth = window.innerWidth - 100;
            const availableHeight = window.innerHeight - 300;
            const scaleX = availableWidth / canvasSize.width;
            const scaleY = availableHeight / canvasSize.height;
            const scale = Math.min(scaleX, scaleY, 1);
            setDisplayScale(scale);
          }} className="zoom-button">
            Fit
          </button>
        </div>
      </div>

      {/* Text Editing Overlay */}
      {editingTextId && (
        <div
          style={{
            position: 'fixed',
            left: textInputPosition.x,
            top: textInputPosition.y,
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.95)',
            border: '2px solid #007bff',
            borderRadius: '8px',
            padding: '8px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <input
            type="text"
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            onKeyDown={handleTextEditKeyDown}
            onBlur={saveTextEdit}
            autoFocus
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: '16px',
              fontFamily: 'Arial',
              minWidth: '150px',
              color: '#333',
            }}
            placeholder="Enter text..."
          />
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            marginTop: '4px',
            fontStyle: 'italic'
          }}>
            Press Enter to save, Esc to cancel
          </div>
        </div>
      )}
    </div>
  );
}

export default FrameEditorPage;